/**
 * Dynamic Subscription Pricing Utility
 * Calculates subscription prices based on category, student range, and billing period
 */

// Pricing matrix: [category][studentRange][billingPeriod]
// Optimized pricing: ₹5-12 per student/month (balanced affordability & sustainability)
// Strategy: Affordable for small schools, scaled pricing for larger institutions
// Updated: Large scale pricing (5000+) adjusted for healthy margins (20-30%)
const PRICING_MATRIX = {
  school: {
    '0-500': { monthly: 3000, quarterly: 9000, yearly: 36000 },
    '500-1000': { monthly: 8500, quarterly: 25500, yearly: 102000 },
    '1000-1500': { monthly: 13000, quarterly: 39000, yearly: 156000 },
    '1500-3000': { monthly: 22000, quarterly: 66000, yearly: 264000 },
    '3000-5000': { monthly: 34000, quarterly: 102000, yearly: 408000 },
    '5000-7000': { monthly: 55000, quarterly: 160000, yearly: 630000 },
    '7000-10000': { monthly: 75000, quarterly: 220000, yearly: 870000 },
    '10000+': { monthly: 110000, quarterly: 320000, yearly: 1260000 }
  },
  college: {
    '0-500': { monthly: 5000, quarterly: 15000, yearly: 60000 },
    '500-1000': { monthly: 10500, quarterly: 31500, yearly: 126000 },
    '1000-1500': { monthly: 16000, quarterly: 48000, yearly: 192000 },
    '1500-3000': { monthly: 27000, quarterly: 81000, yearly: 324000 },
    '3000-5000': { monthly: 43000, quarterly: 129000, yearly: 516000 },
    '5000-7000': { monthly: 62000, quarterly: 182000, yearly: 714000 },
    '7000-10000': { monthly: 85000, quarterly: 250000, yearly: 978000 },
    '10000+': { monthly: 125000, quarterly: 365000, yearly: 1440000 }
  },
  coaching: {
    '0-500': { monthly: 5500, quarterly: 16500, yearly: 66000 },
    '500-1000': { monthly: 11000, quarterly: 33000, yearly: 132000 },
    '1000-1500': { monthly: 17000, quarterly: 51000, yearly: 204000 },
    '1500-3000': { monthly: 29000, quarterly: 87000, yearly: 348000 },
    '3000-5000': { monthly: 46000, quarterly: 138000, yearly: 552000 },
    '5000-7000': { monthly: 68000, quarterly: 200000, yearly: 780000 },
    '7000-10000': { monthly: 92000, quarterly: 270000, yearly: 1068000 },
    '10000+': { monthly: 135000, quarterly: 395000, yearly: 1560000 }
  }
};

// Student range options
export const STUDENT_RANGES = [
  { value: '0-500', label: '0 - 500 students' },
  { value: '500-1000', label: '500 - 1,000 students' },
  { value: '1000-1500', label: '1,000 - 1,500 students' },
  { value: '1500-3000', label: '1,500 - 3,000 students' },
  { value: '3000-5000', label: '3,000 - 5,000 students' },
  { value: '5000-7000', label: '5,000 - 7,000 students' },
  { value: '7000-10000', label: '7,000 - 10,000 students' },
  { value: '10000+', label: '10,000+ students' }
];

// Category options
export const CATEGORIES = [
  { value: 'school', label: 'School' },
  { value: 'college', label: 'College' },
  { value: 'coaching', label: 'Coaching' }
];

// Batch Limits Configuration
// Base batch limits included in subscription by category and student range
const BATCH_LIMITS = {
  school: {
    '0-500': 20,
    '500-1000': 25,
    '1000-1500': 30,
    '1500-3000': 35,
    '3000-5000': 40,
    '5000-7000': 45,
    '7000-10000': 50,
    '10000+': 55
  },
  college: {
    '0-500': 20,
    '500-1000': 25,
    '1000-1500': 30,
    '1500-3000': 35,
    '3000-5000': 40,
    '5000-7000': 45,
    '7000-10000': 50,
    '10000+': 55
  },
  coaching: {
    '0-500': 20,
    '500-1000': 25,
    '1000-1500': 30,
    '1500-3000': 35,
    '3000-5000': 40,
    '5000-7000': 45,
    '7000-10000': 50,
    '10000+': 55
  }
};

// Batch Addon Pack Configuration
export const BATCH_ADDON_CONFIG = {
  batchesPerPack: 10,
  pricePerPack: 1000, // INR per year
  maxTotalBatches: 80, // Maximum batches allowed (base + addons)
  currency: 'INR'
};

/**
 * Get price for a specific category, student range, and billing period
 * @param {string} category - 'school', 'college', or 'coaching'
 * @param {string} studentRange - Student range key (e.g., '0-500')
 * @param {string} billingPeriod - 'monthly', 'quarterly', or 'yearly'
 * @returns {number} Price in INR
 */
export const getPrice = (category, studentRange, billingPeriod) => {
  if (!PRICING_MATRIX[category] || !PRICING_MATRIX[category][studentRange]) {
    // Default to coaching pricing if invalid
    return PRICING_MATRIX.coaching['0-500'][billingPeriod] || 1000;
  }
  return PRICING_MATRIX[category][studentRange][billingPeriod] || 1000;
};

/**
 * Calculate savings for quarterly/annual plans compared to monthly
 * @param {number} monthlyPrice - Monthly price
 * @param {number} planPrice - Plan price (quarterly or yearly)
 * @param {number} duration - Duration in months (3 for quarterly, 12 for yearly)
 * @returns {number} Savings amount
 */
export const calculateSavings = (monthlyPrice, planPrice, duration) => {
  const monthlyEquivalent = monthlyPrice * duration;
  return Math.max(0, monthlyEquivalent - planPrice);
};

/**
 * Get subscription plans with dynamic pricing
 * @param {string} category - 'school', 'college', or 'coaching'
 * @param {string} studentRange - Student range key
 * @returns {Array} Array of plan objects with pricing
 */
export const getSubscriptionPlans = (category, studentRange) => {
  const monthlyPrice = getPrice(category, studentRange, 'monthly');
  const quarterlyPrice = getPrice(category, studentRange, 'quarterly');
  const yearlyPrice = getPrice(category, studentRange, 'yearly');

  const quarterlySavings = calculateSavings(monthlyPrice, quarterlyPrice, 3);
  const yearlySavings = calculateSavings(monthlyPrice, yearlyPrice, 12);

  return [
    {
      id: 'monthly',
      name: 'Monthly Plan',
      price: monthlyPrice,
      originalPrice: monthlyPrice,
      discountPercent: 0,
      period: 'month',
      description: 'Perfect for getting started',
      duration: 1
    },
    {
      id: 'quarterly',
      name: 'Quarterly Plan',
      price: quarterlyPrice,
      originalPrice: quarterlyPrice,
      discountPercent: 0,
      period: '3 months',
      description: quarterlySavings > 0 ? `Save ₹${quarterlySavings.toLocaleString()}` : 'Great for short-term',
      savings: quarterlySavings,
      duration: 3
    },
    {
      id: 'yearly',
      name: 'Yearly Plan',
      price: yearlyPrice,
      originalPrice: yearlyPrice,
      discountPercent: 0,
      period: 'year',
      description: yearlySavings > 0 ? `Best value - Save ₹${yearlySavings.toLocaleString()} (${Math.round((yearlySavings / (monthlyPrice * 12)) * 100)}% off)` : 'Best value',
      savings: yearlySavings,
      duration: 12,
      popular: true
    }
  ];
};

/**
 * Get the appropriate student range based on student count
 * @param {number} studentCount - Current number of students
 * @returns {string} Student range key
 */
export const getStudentRangeForCount = (studentCount) => {
  if (studentCount < 500) return '0-500';
  if (studentCount < 1000) return '500-1000';
  if (studentCount < 1500) return '1000-1500';
  if (studentCount < 3000) return '1500-3000';
  if (studentCount < 5000) return '3000-5000';
  if (studentCount < 7000) return '5000-7000';
  if (studentCount < 10000) return '7000-10000';
  return '10000+';
};

/**
 * Get the maximum student count for a given range
 * @param {string} studentRange - Student range key
 * @returns {number} Maximum student count (or Infinity for 10000+)
 */
export const getMaxStudentsForRange = (studentRange) => {
  switch (studentRange) {
    case '0-500': return 500;
    case '500-1000': return 1000;
    case '1000-1500': return 1500;
    case '1500-3000': return 3000;
    case '3000-5000': return 5000;
    case '5000-7000': return 7000;
    case '7000-10000': return 10000;
    case '10000+': return Infinity;
    default: return 500;
  }
};

/**
 * Get the minimum student count for a given range
 * @param {string} studentRange - Student range key
 * @returns {number} Minimum student count
 */
export const getMinStudentsForRange = (studentRange) => {
  switch (studentRange) {
    case '0-500': return 0;
    case '500-1000': return 500;
    case '1000-1500': return 1000;
    case '1500-3000': return 1500;
    case '3000-5000': return 3000;
    case '5000-7000': return 5000;
    case '7000-10000': return 7000;
    case '10000+': return 10000;
    default: return 0;
  }
};

/**
 * Check if student count is approaching the subscription limit
 * @param {number} studentCount - Current number of students
 * @param {string} currentRange - Current subscription range
 * @returns {Object} Status object with isApproaching, isExceeded, and nextRange
 */
export const checkSubscriptionLimit = (studentCount, currentRange) => {
  const maxStudents = getMaxStudentsForRange(currentRange);
  const nextRange = getStudentRangeForCount(studentCount);
  const isExceeded = studentCount > maxStudents;
  const isApproaching = !isExceeded && studentCount >= maxStudents * 0.9; // 90% threshold
  
  return {
    isApproaching,
    isExceeded,
    nextRange,
    currentRange,
    studentCount,
    maxStudents
  };
};

/**
 * Calculate prorated upgrade cost when student count exceeds current plan
 * @param {string} category - 'school', 'college', or 'coaching'
 * @param {string} currentRange - Current subscription range
 * @param {string} newRange - New subscription range needed
 * @param {string} billingPeriod - 'monthly', 'quarterly', or 'yearly'
 * @param {number} daysRemaining - Days remaining in current subscription
 * @returns {Object} Upgrade cost calculation
 */
export const calculateUpgradeCost = (category, currentRange, newRange, billingPeriod, daysRemaining) => {
  const currentPrice = getPrice(category, currentRange, billingPeriod);
  const newPrice = getPrice(category, newRange, billingPeriod);
  
  const priceDifference = newPrice - currentPrice;
  
  // Calculate total days in billing period
  const totalDays = billingPeriod === 'monthly' ? 30 : billingPeriod === 'quarterly' ? 90 : 365;
  
  // Prorated cost for remaining period
  const proratedCost = Math.round((priceDifference * daysRemaining) / totalDays);
  
  return {
    currentPrice: currentPrice,
    newPrice: newPrice,
    priceDifference,
    daysRemaining,
    totalDays,
    proratedCost: Math.max(0, proratedCost), // Never negative
    upgradeRequired: priceDifference > 0,
    message: proratedCost > 0 
      ? `Pay ₹${proratedCost.toLocaleString()} for remaining ${daysRemaining} days`
      : 'No additional payment required'
  };
};

/**
 * Get upgrade options when student limit is exceeded
 * @param {number} studentCount - Current number of students
 * @param {string} category - 'school', 'college', or 'coaching'
 * @param {string} currentRange - Current subscription range
 * @param {string} billingPeriod - 'monthly', 'quarterly', or 'yearly'
 * @param {Date} subscriptionEndDate - Current subscription end date
 * @returns {Object} Upgrade information and options
 */
export const getUpgradeOptions = (studentCount, category, currentRange, billingPeriod, subscriptionEndDate) => {
  const limitStatus = checkSubscriptionLimit(studentCount, currentRange);
  
  if (!limitStatus.isExceeded && !limitStatus.isApproaching) {
    return {
      needsUpgrade: false,
      message: 'Your current plan is sufficient'
    };
  }
  
  const now = new Date();
  const endDate = subscriptionEndDate instanceof Date ? subscriptionEndDate : new Date(subscriptionEndDate);
  const daysRemaining = Math.max(0, Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)));
  
  const upgradeCost = calculateUpgradeCost(
    category,
    currentRange,
    limitStatus.nextRange,
    billingPeriod,
    daysRemaining
  );
  
  return {
    needsUpgrade: limitStatus.isExceeded,
    isApproaching: limitStatus.isApproaching,
    currentRange,
    suggestedRange: limitStatus.nextRange,
    studentCount,
    maxStudents: limitStatus.maxStudents,
    upgradeCost,
    warningMessage: limitStatus.isExceeded 
      ? `⚠️ You have ${studentCount} students but your plan supports only ${limitStatus.maxStudents}. Please upgrade to continue.`
      : `⚠️ You're approaching your limit (${studentCount}/${limitStatus.maxStudents} students). Consider upgrading soon.`,
    actionRequired: limitStatus.isExceeded
  };
};

/**
 * Check if institute can add more students based on their plan limit
 * @param {number} currentStudentCount - Current number of students
 * @param {string} subscriptionRange - Current subscription range
 * @returns {Object} Status with canAdd, remaining, and message
 */
export const canAddStudent = (currentStudentCount, subscriptionRange) => {
  const maxStudents = getMaxStudentsForRange(subscriptionRange);
  const remaining = maxStudents === Infinity ? Infinity : maxStudents - currentStudentCount;
  const canAdd = remaining > 0;
  
  // Warning when approaching limit (90%)
  const isApproaching = !canAdd && remaining <= maxStudents * 0.1 && remaining > 0;
  
  return {
    canAdd,
    remaining: remaining === Infinity ? 'Unlimited' : remaining,
    currentCount: currentStudentCount,
    maxAllowed: maxStudents === Infinity ? 'Unlimited' : maxStudents,
    isApproaching,
    message: canAdd 
      ? (isApproaching 
          ? `⚠️ Approaching limit: ${remaining} student slots remaining`
          : `${remaining === Infinity ? 'Unlimited' : remaining} student slots available`)
      : `🚫 Student limit reached (${currentStudentCount}/${maxStudents}). Please upgrade your plan or contact support@mahstry.com`,
    actionRequired: !canAdd
  };
};

/**
 * Get base batch limit for a category and student range
 * @param {string} category - 'school', 'college', or 'coaching'
 * @param {string} studentRange - Student range key
 * @returns {number} Base batch limit
 */
export const getBaseBatchLimit = (category, studentRange) => {
  if (!BATCH_LIMITS[category] || !BATCH_LIMITS[category][studentRange]) {
    return 20; // Default minimum
  }
  return BATCH_LIMITS[category][studentRange];
};

/**
 * Calculate total available batches (base + purchased addons)
 * @param {string} category - 'school', 'college', or 'coaching'
 * @param {string} studentRange - Student range key
 * @param {number} purchasedAddonPacks - Number of addon packs purchased (default: 0)
 * @returns {Object} Batch limit information
 */
export const getTotalBatchLimit = (category, studentRange, purchasedAddonPacks = 0) => {
  const baseBatches = getBaseBatchLimit(category, studentRange);
  const addonBatches = purchasedAddonPacks * BATCH_ADDON_CONFIG.batchesPerPack;
  const totalBatches = Math.min(baseBatches + addonBatches, BATCH_ADDON_CONFIG.maxTotalBatches);
  
  return {
    baseBatches,
    addonBatches,
    totalBatches,
    maxPossible: BATCH_ADDON_CONFIG.maxTotalBatches,
    canAddMore: totalBatches < BATCH_ADDON_CONFIG.maxTotalBatches
  };
};

/**
 * Check if institution can create more batches
 * @param {number} currentBatchCount - Current number of batches
 * @param {string} category - 'school', 'college', or 'coaching'
 * @param {string} studentRange - Student range key
 * @param {number} purchasedAddonPacks - Number of addon packs purchased
 * @returns {Object} Status with canCreate, remaining, and needsAddon
 */
export const checkBatchLimit = (currentBatchCount, category, studentRange, purchasedAddonPacks = 0) => {
  const limits = getTotalBatchLimit(category, studentRange, purchasedAddonPacks);
  const remaining = limits.totalBatches - currentBatchCount;
  const canCreate = remaining > 0;
  const needsAddon = !canCreate && limits.canAddMore;
  const isAtMaximum = limits.totalBatches >= BATCH_ADDON_CONFIG.maxTotalBatches;
  
  return {
    canCreate,
    remaining,
    needsAddon,
    isAtMaximum,
    currentCount: currentBatchCount,
    totalLimit: limits.totalBatches,
    baseBatches: limits.baseBatches,
    addonBatches: limits.addonBatches
  };
};

/**
 * Calculate how many addon packs are needed for desired batch count
 * @param {number} desiredBatchCount - Desired total batch count
 * @param {string} category - 'school', 'college', or 'coaching'
 * @param {string} studentRange - Student range key
 * @param {number} currentAddonPacks - Current addon packs owned
 * @returns {Object} Addon pack calculation
 */
export const calculateAddonPacksNeeded = (desiredBatchCount, category, studentRange, currentAddonPacks = 0) => {
  const baseBatches = getBaseBatchLimit(category, studentRange);
  const currentTotal = getTotalBatchLimit(category, studentRange, currentAddonPacks).totalBatches;
  
  if (desiredBatchCount <= currentTotal) {
    return {
      packsNeeded: 0,
      totalCost: 0,
      newTotal: currentTotal
    };
  }
  
  const additionalBatchesNeeded = desiredBatchCount - currentTotal;
  const packsNeeded = Math.ceil(additionalBatchesNeeded / BATCH_ADDON_CONFIG.batchesPerPack);
  const newAddonPacks = currentAddonPacks + packsNeeded;
  const newTotal = Math.min(
    baseBatches + (newAddonPacks * BATCH_ADDON_CONFIG.batchesPerPack),
    BATCH_ADDON_CONFIG.maxTotalBatches
  );
  
  return {
    packsNeeded,
    totalCost: packsNeeded * BATCH_ADDON_CONFIG.pricePerPack,
    newTotal,
    pricePerPack: BATCH_ADDON_CONFIG.pricePerPack,
    batchesPerPack: BATCH_ADDON_CONFIG.batchesPerPack
  };
};

/**
 * Get maximum addon packs that can be purchased
 * @param {string} category - 'school', 'college', or 'coaching'
 * @param {string} studentRange - Student range key
 * @param {number} currentAddonPacks - Current addon packs owned
 * @returns {Object} Maximum addon pack information
 */
export const getMaxAddonPacks = (category, studentRange, currentAddonPacks = 0) => {
  const baseBatches = getBaseBatchLimit(category, studentRange);
  const remainingBatches = BATCH_ADDON_CONFIG.maxTotalBatches - baseBatches;
  const maxPossiblePacks = Math.floor(remainingBatches / BATCH_ADDON_CONFIG.batchesPerPack);
  const canPurchaseMore = currentAddonPacks < maxPossiblePacks;
  const additionalPacksAvailable = Math.max(0, maxPossiblePacks - currentAddonPacks);
  
  return {
    maxPossiblePacks,
    currentPacks: currentAddonPacks,
    canPurchaseMore,
    additionalPacksAvailable,
    totalCostForMax: additionalPacksAvailable * BATCH_ADDON_CONFIG.pricePerPack
  };
};

